<template>
  <v-container fluid class="pa-4">
    <!-- Header Section -->
    <v-card class="mb-4 elevation-3" color="primary" dark>
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon size="32" class="mr-3">mdi-clipboard-list</v-icon>
          <div>
            <div class="text-h5 font-weight-bold">Inspecciones Vehiculares</div>
            <div class="text-subtitle-1 opacity-8">Gestión y control de inspecciones</div>
          </div>
        </div>
        <v-btn
          color="white"
          outlined
          large
          @click="navigateToNewInspection"
          class="font-weight-bold"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          Nueva Inspección
        </v-btn>
      </v-card-title>
    </v-card>

    <!-- Statistics Cards -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card" color="blue" dark elevation="2">
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-file-document</v-icon>
            <div class="text-h4 font-weight-bold">{{ stats.total }}</div>
            <div class="text-caption">Total Inspecciones</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card" color="success" dark elevation="2">
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-check-circle</v-icon>
            <div class="text-h4 font-weight-bold">{{ stats.completed }}</div>
            <div class="text-caption">Completadas</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card" color="warning" dark elevation="2">
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-alert-circle</v-icon>
            <div class="text-h4 font-weight-bold">{{ stats.withDamages }}</div>
            <div class="text-caption">Con Daños</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card" color="info" dark elevation="2">
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-calendar-today</v-icon>
            <div class="text-h4 font-weight-bold">{{ stats.today }}</div>
            <div class="text-caption">Hoy</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card class="mb-4 elevation-2">
      <v-card-title class="pb-2">
        <v-icon left>mdi-filter</v-icon>
        <span class="ml-2 font-weight-medium">Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.plate"
              label="Placa"
              prepend-icon="mdi-car"
              outlined
              dense
              clearable
              @input="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.client"
              label="Cliente"
              prepend-icon="mdi-account"
              outlined
              dense
              clearable
              @input="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Estado"
              prepend-icon="mdi-flag"
              outlined
              dense
              clearable
              @change="applyFilters"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-menu
              v-model="datePicker"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.date"
                  label="Fecha"
                  prepend-icon="mdi-calendar"
                  readonly
                  outlined
                  dense
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearDateFilter"
                />
              </template>
              <v-date-picker
                v-model="filters.date"
                @input="datePicker = false"
                @change="applyFilters"
                locale="es"
                no-title
                scrollable
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="1">
            <v-btn
              color="primary"
              block
              @click="loadInspections"
              :loading="loading"
              :disabled="loading"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-card elevation="2">
      <v-card-title class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon left>mdi-table</v-icon>
          Lista de Inspecciones
        </div>
        <div class="text-caption grey--text">
          Mostrando {{ filteredInspections.length }} de {{ inspections.length }} registros
        </div>
      </v-card-title>

      <v-card-text class="pa-0">
        <v-data-table
          :headers="tableHeaders"
          :items="filteredInspections"
          :loading="loading"
          :items-per-page="itemsPerPage"
          :footer-props="tableFooterProps"
          class="elevation-0"
          no-data-text="No hay inspecciones registradas"
          loading-text="Cargando inspecciones..."
        >
          <!-- Inspection Number -->
          <template v-slot:item.id="{ item }">
            <v-chip small color="primary" outlined class="font-weight-bold">
              #{{ item.id }}
            </v-chip>
          </template>

          <!-- Date -->
          <template v-slot:item.fecha="{ item }">
            <div class="d-flex flex-column">
              <span class="font-weight-medium">{{ formatDate(item.fecha) }}</span>
              <span class="text-caption grey--text">{{ formatTime(item.fecha) }}</span>
            </div>
          </template>

          <!-- Client -->
          <template v-slot:item.cliente_nombre="{ item }">
            <div class="d-flex flex-column">
              <span class="font-weight-medium">
                {{ item.client_name || item.cliente_nombre || "N/A" }}
              </span>
              <span class="text-caption grey--text">
                {{ item.client_document || item.cliente_documento || "" }}
              </span>
            </div>
          </template>

          <!-- Vehicle -->
          <template v-slot:item.vehiculo_placa="{ item }">
            <div class="d-flex flex-column">
              <v-chip small outlined color="blue-grey" class="mb-1 font-weight-bold">
                <v-icon left small>mdi-car</v-icon>
                {{ item.plate || item.vehiculo_placa || "N/A" }}
              </v-chip>
              <span class="text-caption">
                {{ item.vehicle_brand || item.vehiculo_marca || "" }}
                {{ item.vehicle_model || item.vehiculo_modelo || "" }}
              </span>
            </div>
          </template>

          <!-- Inspector -->
          <template v-slot:item.inspector="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" color="primary" class="mr-2">
                <v-icon small dark>mdi-account</v-icon>
              </v-avatar>
              <span>{{ item.inspector_name || item.inspector || "N/A" }}</span>
            </div>
          </template>

          <!-- Status -->
          <template v-slot:item.estado="{ item }">
            <v-chip
              small
              :color="getStatusColor(item.estado)"
              dark
              class="font-weight-medium"
            >
              <v-icon left small>{{ getStatusIcon(item.estado) }}</v-icon>
              {{ getStatusText(item.estado) }}
            </v-chip>
          </template>

          <!-- Damages -->
          <template v-slot:item.damages_count="{ item }">
            <v-chip
              small
              :color="getDamagesCount(item) > 0 ? 'error' : 'success'"
              outlined
              class="font-weight-bold"
            >
              <v-icon left small>
                {{ getDamagesCount(item) > 0 ? "mdi-alert-circle" : "mdi-check-circle" }}
              </v-icon>
              {{ getDamagesCount(item) }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="viewInspection(item)"
                  >
                    <v-icon small>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Ver Detalles</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                    @click="editInspection(item)"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Editar</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="success"
                    v-bind="attrs"
                    v-on="on"
                    @click="downloadReport(item)"
                  >
                    <v-icon small>mdi-file-pdf-box</v-icon>
                  </v-btn>
                </template>
                <span>Descargar PDF</span>
              </v-tooltip>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmDelete(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Eliminar</span>
              </v-tooltip>
            </div>
          </template>

          <!-- No Data Slot -->
          <template v-slot:no-data>
            <div class="text-center pa-8">
              <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
              <div class="text-h6 grey--text mt-4">No hay inspecciones registradas</div>
              <div class="text-body-1 grey--text mt-2">
                Comience creando su primera inspección vehicular
              </div>
              <v-btn color="primary" large class="mt-4" @click="navigateToNewInspection">
                <v-icon left>mdi-plus</v-icon>
                Crear Primera Inspección
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline error white--text">
          <v-icon left color="white" size="28">mdi-alert-circle</v-icon>
          Confirmar Eliminación
        </v-card-title>
        <v-card-text class="text-body-1 pt-4">
          ¿Está seguro de que desea eliminar la inspección
          <strong>#{{ itemToDelete ? itemToDelete.id : "" }}</strong>
          del vehículo con placa
          <strong>{{ getItemPlate(itemToDelete) }}</strong
          >?
          <v-alert type="warning" dense class="mt-4" border="left">
            <strong>Esta acción no se puede deshacer.</strong>
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDelete" :disabled="deleting"> Cancelar </v-btn>
          <v-btn
            color="error"
            @click="executeDelete"
            :loading="deleting"
            :disabled="deleting"
          >
            <v-icon left>mdi-delete</v-icon>
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      <v-icon left dark>{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text dark v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { webserver } from "@/services/webserver.js";

export default {
  name: "VehicleInspectionList",

  data() {
    return {
      // Loading states
      loading: false,
      deleting: false,

      // Dialog states
      deleteDialog: false,
      datePicker: false,

      // Data
      inspections: [],
      filteredInspections: [],
      itemToDelete: null,

      // Filters
      filters: {
        plate: "",
        client: "",
        status: null,
        date: null,
      },

      // Statistics
      stats: {
        total: 0,
        completed: 0,
        withDamages: 0,
        today: 0,
      },

      // Table configuration
      itemsPerPage: 10,

      // Snackbar
      snackbar: {
        show: false,
        message: "",
        color: "success",
        icon: "mdi-check-circle",
        timeout: 4000,
      },
    };
  },

  computed: {
    statusOptions() {
      return [
        { text: "Todos", value: null },
        { text: "Borrador", value: "draft" },
        { text: "En Progreso", value: "in_progress" },
        { text: "Completado", value: "completed" },
        { text: "Cancelado", value: "cancelled" },
      ];
    },

    tableHeaders() {
      return [
        {
          text: "#",
          value: "id",
          width: "80px",
          align: "center",
          sortable: true,
        },
        {
          text: "Fecha",
          value: "fecha",
          width: "150px",
          sortable: true,
        },
        {
          text: "Cliente",
          value: "cliente_nombre",
          width: "200px",
          sortable: true,
        },
        {
          text: "Vehículo",
          value: "vehiculo_placa",
          width: "180px",
          sortable: true,
        },
        {
          text: "Inspector",
          value: "inspector",
          width: "150px",
          sortable: true,
        },
        {
          text: "Estado",
          value: "estado",
          width: "130px",
          align: "center",
          sortable: true,
        },
        {
          text: "Daños",
          value: "damages_count",
          width: "100px",
          align: "center",
          sortable: true,
        },
        {
          text: "Acciones",
          value: "actions",
          width: "200px",
          align: "center",
          sortable: false,
        },
      ];
    },

    tableFooterProps() {
      return {
        "items-per-page-options": [5, 10, 25, 50, 100],
        "items-per-page-text": "Registros por página:",
      };
    },
  },

  mounted() {
    this.initializeComponent();
  },

  methods: {
    async initializeComponent() {
      try {
        await this.waitForStoreData();
        await this.loadInspections();
      } catch (error) {
        console.error("Error initializing component:", error);
        this.showSnackbar("Error al inicializar el componente", "error");
      }
    },

    async waitForStoreData() {
      return new Promise((resolve, reject) => {
        const maxAttempts = 50;
        let attempts = 0;

        const checkStore = () => {
          attempts++;
          if (window.store || window.profile) {
            return resolve();
          }
          if (attempts >= maxAttempts) {
            return reject(new Error("Timeout waiting for store"));
          }
          setTimeout(checkStore, 100);
        };

        checkStore();
      });
    },

    async loadInspections() {
      this.loading = true;
      try {
        const filters = [];
        filters.push({
          field: "vehiculo_placa",
          operator: "<>",
          value: ``,
        });

        // Build filters array
        if (this.filters.plate) {
          filters.push({
            field: "vehiculo_placa",
            operator: "LIKE",
            value: `%${this.filters.plate}%`,
          });
        }

        if (this.filters.client) {
          filters.push({
            field: "cliente_nombre",
            operator: "LIKE",
            value: `%${this.filters.client}%`,
          });
        }

        if (this.filters.status) {
          filters.push({
            field: "estado",
            operator: "=",
            value: this.filters.status,
          });
        }

        if (this.filters.date) {
          filters.push({
            field: "DATE(fecha)",
            operator: "=",
            value: this.filters.date,
          });
        }

        const storeId = this.getStoreId();
        if (!storeId) {
          throw new Error("No store ID available");
        }

        const query = {
          store: storeId,
          table: "vehicle_inspections",
          filters: filters,
        };

        webserver("get_dataTable", query, (response) => {
          this.loading = false;

          if (response && response.error) {
            this.showSnackbar(`Error: ${response.message}`, "error");
            return;
          }

          this.inspections = Array.isArray(response) ? response : [];
          this.applyFilters();
          this.calculateStats();
        });
      } catch (error) {
        this.loading = false;
        console.error("Error loading inspections:", error);
        this.showSnackbar(`Error al cargar inspecciones: ${error.message}`, "error");
      }
    },

    applyFilters() {
      try {
        let filtered = [...this.inspections];

        // Apply local filters (already applied in loadInspections, but kept for consistency)
        if (this.filters.plate) {
          const plateFilter = this.filters.plate.toLowerCase();
          filtered = filtered.filter((item) =>
            (item.vehiculo_placa || item.plate || "").toLowerCase().includes(plateFilter)
          );
        }

        if (this.filters.client) {
          const clientFilter = this.filters.client.toLowerCase();
          filtered = filtered.filter((item) =>
            (item.cliente_nombre || item.client_name || "")
              .toLowerCase()
              .includes(clientFilter)
          );
        }

        if (this.filters.status) {
          filtered = filtered.filter(
            (item) => (item.estado || "draft") === this.filters.status
          );
        }

        if (this.filters.date) {
          filtered = filtered.filter((item) => {
            const dateStr = item.fecha || item.created_at || "";
            const itemDate =
              dateStr.split && dateStr.split(" ")[0] ? dateStr.split(" ")[0] : "";
            return itemDate === this.filters.date;
          });
        }

        this.filteredInspections = filtered;
      } catch (error) {
        console.error("Error applying filters:", error);
        this.filteredInspections = [...this.inspections];
      }
    },

    calculateStats() {
      try {
        this.stats.total = this.inspections.length;

        // Count completed inspections
        this.stats.completed = this.inspections.filter(
          (i) => (i.estado || i.status || "draft") === "completed"
        ).length;

        // Count inspections with damages
        this.stats.withDamages = this.inspections.filter((i) => {
          const damagesCount = i.damages_count || (i.damages ? i.damages.length : 0) || 0;
          return damagesCount > 0;
        }).length;

        // Count today's inspections
        const today = new Date().toISOString().split("T")[0];
        this.stats.today = this.inspections.filter((i) => {
          const dateStr = i.fecha || i.created_at || "";
          const itemDate =
            dateStr.split && dateStr.split(" ")[0] ? dateStr.split(" ")[0] : "";
          return itemDate === today;
        }).length;
      } catch (error) {
        console.error("Error calculating stats:", error);
        this.stats = { total: 0, completed: 0, withDamages: 0, today: 0 };
      }
    },

    navigateToNewInspection() {
      try {
        // Try router navigation first
        if (this.$router) {
          this.$router.push({ name: "VehicleInspection" }).catch((err) => {
            console.warn("Router navigation failed:", err);
            window.location.href = "/vehicle-inspection";
          });
        } else {
          window.location.href = "/vehicle-inspection";
        }
      } catch (error) {
        console.error("Error navigating to new inspection:", error);
        this.showSnackbar("Error al navegar a nueva inspección", "error");
      }
    },

    viewInspection(item) {
      try {
        if (this.$router) {
          // Pass the complete item data through params
          this.$router
            .push({
              name: "VehicleInspection",
              params: {
                id: item.id,
                inspectionData: item,
              },
            })
            .catch((err) => {
              console.warn("Router navigation failed:", err);
              window.location.href = `/vehicle-inspection?id=${item.id}`;
            });
        } else {
          window.location.href = `/vehicle-inspection?id=${item.id}`;
        }
      } catch (error) {
        console.error("Error viewing inspection:", error);
        this.showSnackbar("Error al ver la inspección", "error");
      }
    },

    editInspection(item) {
      try {
        if (this.$router) {
          // Pass the complete item data through params - NO API CALL NEEDED!
          this.$router
            .push({
              name: "VehicleInspection",
              params: {
                id: item.id,
                inspectionData: item, // Pass the complete row data
              },
            })
            .catch((err) => {
              console.warn("Router navigation failed:", err);
              window.location.href = `/vehicle-inspection?id=${item.id}`;
            });
        } else {
          window.location.href = `/vehicle-inspection?id=${item.id}`;
        }
      } catch (error) {
        console.error("Error editing inspection:", error);
        this.showSnackbar("Error al editar la inspección", "error");
      }
    },

    downloadReport(item) {
      try {
        this.showSnackbar(
          `Generando reporte PDF para inspección #${item.id}...`,
          "info",
          "mdi-download"
        );

        const storeId = this.getStoreId();
        const downloadUrl = `/api/vehicle-inspections/${item.id}/pdf?store=${storeId}`;

        window.open(downloadUrl, "_blank");
      } catch (error) {
        console.error("Error downloading report:", error);
        this.showSnackbar("Error al descargar el reporte", "error");
      }
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },

    cancelDelete() {
      this.itemToDelete = null;
      this.deleteDialog = false;
      this.deleting = false;
    },

    executeDelete() {
      if (!this.itemToDelete) {
        this.cancelDelete();
        return;
      }

      this.deleting = true;

      try {
        const storeId = this.getStoreId();
        if (!storeId) {
          throw new Error("No store ID available");
        }

        const query = {
          store: storeId,
          table: "vehicle_inspections",
          filters: [
            {
              field: "id",
              operator: "=",
              value: this.itemToDelete.id,
            },
          ],
        };

        webserver("delete_records", query, (response) => {
          this.deleting = false;

          if (response && response.error) {
            this.showSnackbar(`Error al eliminar: ${response.message}`, "error");
          } else {
            this.showSnackbar("Inspección eliminada exitosamente", "success");
            this.loadInspections();
          }

          this.cancelDelete();
        });
      } catch (error) {
        this.deleting = false;
        console.error("Error deleting inspection:", error);
        this.showSnackbar("Error al eliminar la inspección", "error");
        this.cancelDelete();
      }
    },

    clearDateFilter() {
      this.filters.date = null;
      this.applyFilters();
    },

    // Utility methods
    getStoreId() {
      return window.store?.store_id || window.profile?.store_id || null;
    },

    getItemPlate(item) {
      if (!item) return "N/A";
      return item.plate || item.vehiculo_placa || "N/A";
    },

    getDamagesCount(item) {
      if (!item) return 0;
      return item.damages_count || (item.damages ? item.damages.length : 0) || 0;
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("es-ES", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        console.error("Error formatting date:", error);
        return "N/A";
      }
    },

    formatTime(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleTimeString("es-ES", {
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        console.error("Error formatting time:", error);
        return "";
      }
    },

    getStatusColor(status) {
      const colors = {
        draft: "grey",
        borrador: "grey",
        in_progress: "warning",
        completed: "success",
        completado: "success",
        cancelled: "error",
      };
      return colors[status || "draft"] || "grey";
    },

    getStatusIcon(status) {
      const icons = {
        draft: "mdi-file-document-outline",
        borrador: "mdi-file-document-outline",
        in_progress: "mdi-clock-outline",
        completed: "mdi-check-circle",
        completado: "mdi-check-circle",
        cancelled: "mdi-close-circle",
      };
      return icons[status || "draft"] || "mdi-file-document-outline";
    },

    getStatusText(status) {
      const texts = {
        draft: "Borrador",
        borrador: "Borrador",
        in_progress: "En Progreso",
        completed: "Completado",
        completado: "Completado",
        cancelled: "Cancelado",
      };
      return texts[status || "draft"] || "Borrador";
    },

    showSnackbar(message, color = "success", icon = null, timeout = 4000) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.icon = icon || this.getSnackbarIcon(color);
      this.snackbar.timeout = timeout;
      this.snackbar.show = true;
    },

    getSnackbarIcon(color) {
      const icons = {
        success: "mdi-check-circle",
        error: "mdi-alert-circle",
        warning: "mdi-alert",
        info: "mdi-information",
      };
      return icons[color] || "mdi-information";
    },
  },
};
</script>

<style scoped>
.stat-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

.v-data-table {
  font-size: 0.875rem;
}

.v-chip {
  font-size: 0.75rem;
}

.theme--dark .v-card {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.opacity-8 {
  opacity: 0.8;
}

@media (max-width: 960px) {
  .v-card-title .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 12px;
  }

  .v-card-title .text-h5 {
    font-size: 1.25rem !important;
  }

  .v-btn.large {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .stat-card .text-h4 {
    font-size: 1.5rem !important;
  }
}
</style>
